import { useEffect, useState } from "react";
import { API, apiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Save, Building2, AlertTriangle, Trash2, Download } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, refreshSettings } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState({ name: "", address: "", gst: "", contact: "", email: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetModal, setResetModal] = useState(false);
  const [resetForm, setResetForm] = useState({
    confirm: "", sales: true, purchases: true, expenses: true,
    vendors: false, customers: false, medicines: false, activity_log: false,
  });
  const [resetting, setResetting] = useState(false);
  const [backingUp, setBackingUp] = useState(false);

  const downloadBackup = async () => {
    setBackingUp(true);
    try {
      const res = await API.get("/backup", { responseType: "blob" });
      // extract filename from content-disposition if present
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `medicore_backup_${new Date().toISOString().slice(0,10)}.json`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBackingUp(false); }
  };

  useEffect(() => {
    API.get("/settings").then((r) => setForm({
      name: r.data.name || "", address: r.data.address || "",
      gst: r.data.gst || "", contact: r.data.contact || "", email: r.data.email || "",
    })).finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put("/settings", form);
      await refreshSettings();
      toast.success("Store settings updated. Sidebar, invoices and reports are now using these details.");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 sm:p-6 md:p-8 max-w-3xl" data-testid="settings-page">
      <div className="mb-6">
        <div className="overline mb-2">Configuration</div>
        <h1 className="font-display text-3xl font-bold">Store Settings</h1>
        <p className="text-sm text-slate-500 mt-1">These details appear on every printed invoice and exported report.</p>
      </div>

      <form onSubmit={save} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#002FA7]/5 text-[#002FA7] flex items-center justify-center">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <div className="font-display font-semibold">Business Identity</div>
            <div className="text-xs text-slate-500">Name, address and tax details</div>
          </div>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <L label="Company / Store Name *">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="settings-name-input" className="inp" />
          </L>
          <L label="GST Number">
            <input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })}
                   className="inp font-mono text-sm" placeholder="07ABCDE1234F1Z5" />
          </L>
          <L label="Address *" full>
            <textarea required rows={3} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                      data-testid="settings-address-input" className="inp resize-none" />
          </L>
          <L label="Contact Number">
            <input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="inp" />
          </L>
          <L label="Email">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" />
          </L>
        </div>
        <div className="p-5 border-t border-slate-200 flex justify-end">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="save-settings-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.55rem .75rem;font-size:.875rem;outline:none;font-family:inherit}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>

      <div className="mt-8 bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="backup-card">
        <div className="p-5 border-b border-slate-100 bg-emerald-50/40 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Download className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-semibold">Data Backup</h3>
            <p className="text-xs text-slate-500">
              {user?.branch_id
                ? "Download a JSON snapshot of your branch — medicines, sales, purchases, expenses, customers, vendors and ledgers."
                : "Full platform backup across all branches (admin only)."}
            </p>
          </div>
        </div>
        <div className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-slate-600 max-w-lg">
            The backup is a portable JSON file — keep it safe (e.g. cloud drive). Users, credentials and login history are excluded for security.
          </div>
          <button onClick={downloadBackup} disabled={backingUp} data-testid="download-backup-btn"
                  className="btn-primary">
            <Download className="w-4 h-4" /> {backingUp ? "Preparing…" : "Download Backup"}
          </button>
        </div>
      </div>

      {isAdmin && (
        <div className="mt-10 bg-white border border-red-200 rounded-xl overflow-hidden" data-testid="danger-zone">
          <div className="p-5 border-b border-red-100 bg-red-50/50 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div>
              <h3 className="font-display text-lg font-semibold text-red-900">Danger Zone</h3>
              <p className="text-xs text-red-700">Irreversible actions that affect every branch.</p>
            </div>
          </div>
          <div className="p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-semibold text-sm">Reset Transactional Data</div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                Wipes sales, purchases, expenses, ledger entries across <b>all branches</b>.
                Medicine list, users, branches and settings are <b>preserved</b>.
              </p>
            </div>
            <button onClick={() => setResetModal(true)} data-testid="open-reset-modal"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] text-white text-sm font-medium">
              <Trash2 className="w-4 h-4" /> Reset Data…
            </button>
          </div>
        </div>
      )}

      {resetModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reset-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-5 border-b border-slate-200 flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="font-display text-lg font-semibold">Reset Transactional Data</h3>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <p className="text-slate-600">Choose what to erase. Medicines, users, branches and settings are always preserved.</p>
              <div className="space-y-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
                {[
                  ["sales", "Sales (invoices)"],
                  ["purchases", "Purchases (POs)"],
                  ["expenses", "Expenses"],
                  ["customers", "Customers (masters)"],
                  ["vendors", "Vendors (masters)"],
                  ["medicines", "🚨 Medicines (full stock wipe — ALL branches)"],
                  ["activity_log", "Activity log"],
                ].map(([k, label]) => (
                  <label key={k} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={resetForm[k]} data-testid={`reset-${k}`}
                           onChange={(e) => setResetForm((f) => ({ ...f, [k]: e.target.checked }))} />
                    <span className={k === "medicines" ? "text-red-700 font-semibold" : ""}>{label}</span>
                  </label>
                ))}
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">
                  Type <code className="font-mono font-bold text-red-700">RESET</code> to confirm
                </div>
                <input value={resetForm.confirm}
                       onChange={(e) => setResetForm((f) => ({ ...f, confirm: e.target.value }))}
                       data-testid="reset-confirm-input"
                       className="inp font-mono"
                       placeholder="RESET" autoComplete="off" />
              </div>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => { setResetModal(false); setResetForm({ confirm: "", sales: true, purchases: true, expenses: true, vendors: false, customers: false, medicines: false, activity_log: false }); }}
                      className="btn-ghost">Cancel</button>
              <button onClick={async () => {
                if (resetForm.confirm.trim().toUpperCase() !== "RESET") {
                  toast.error("Please type RESET to confirm");
                  return;
                }
                setResetting(true);
                try {
                  const { data } = await API.post("/admin/reset-data", resetForm);
                  const summary = Object.entries(data.deleted_counts || {}).map(([k, n]) => `${n} ${k}`).join(", ") || "nothing matched";
                  toast.success(`Reset complete — ${summary}`);
                  setResetModal(false);
                  setResetForm({ confirm: "", sales: true, purchases: true, expenses: true, vendors: false, customers: false, medicines: false, activity_log: false });
                } catch (e) { toast.error(apiError(e)); }
                finally { setResetting(false); }
              }} disabled={resetting || resetForm.confirm.trim().toUpperCase() !== "RESET"}
                 data-testid="confirm-reset-btn"
                 className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#B91C1C] hover:bg-[#991B1B] disabled:opacity-50 text-white text-sm font-medium">
                <Trash2 className="w-4 h-4" /> {resetting ? "Erasing…" : "Erase Selected Data"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function L({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
